// DispatchDeck — OSHA 1910.1020 Records Access Helpers
// CRUD for the per-tenant recordsAccessLog collection. Used by the modal
// surfaced from Settings → Compliance when the Track OSHA Records toggle
// is on. Mirrors the slipHelpers / gpsPing utility pattern.

import {
  db,
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  Timestamp,
  query,
  orderBy
} from '../firebase-config.js?v=1868';

function resolveTenant(tenant) {
  return tenant === 'demo-tenant' ? 'demo' : tenant;
}

// ============================================
// CREATE — log a new records access request
// requesterRef: { personId?, name, email?, phone? }
// recordType: 'medical' | 'exposure' | 'analysis'
// requestedFor: { personId?, name }  — whose records, defaults to requester
// ============================================
export async function createAccessRequest(tenantRaw, opts = {}) {
  const tenant = resolveTenant(tenantRaw);
  const docToWrite = {
    requesterPersonId: opts.requesterPersonId || null,
    requesterName: opts.requesterName || '',
    requesterEmail: opts.requesterEmail || null,
    requesterPhone: opts.requesterPhone || null,
    requestedForPersonId: opts.requestedForPersonId || opts.requesterPersonId || null,
    requestedForName: opts.requestedForName || opts.requesterName || '',
    recordType: opts.recordType || 'medical', // medical | exposure | analysis
    requestSource: opts.requestSource || 'employee', // employee | designated_rep | osha
    designatedRepName: opts.designatedRepName || null,
    designatedRepAuthOnFile: opts.designatedRepAuthOnFile === true,
    requestedAt: opts.requestedAt
      ? Timestamp.fromDate(new Date(opts.requestedAt))
      : Timestamp.now(),
    fulfilledAt: null,
    fulfilledBy: null,
    deliveryMethod: null, // copy_email | copy_print | inspection_in_person | mailed | denied
    identifiersScrubbed: null,
    notes: opts.notes || '',
    status: 'pending', // pending | fulfilled | denied | voided
    createdAt: Timestamp.now(),
    createdBy: window.AppState?.user?.email || 'unknown',
    voidedAt: null,
    voidedBy: null
  };
  const ref = await addDoc(
    collection(db, 'tenants', tenant, 'recordsAccessLog'),
    docToWrite
  );
  return { id: ref.id, ...docToWrite };
}

// ============================================
// LIST — load all access log entries, newest first
// ============================================
export async function listAccessLog(tenantRaw) {
  const tenant = resolveTenant(tenantRaw);
  const q = query(
    collection(db, 'tenants', tenant, 'recordsAccessLog'),
    orderBy('requestedAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ============================================
// MARK FULFILLED
// ============================================
export async function markFulfilled(tenantRaw, entryId, opts = {}) {
  const tenant = resolveTenant(tenantRaw);
  await updateDoc(
    doc(db, 'tenants', tenant, 'recordsAccessLog', entryId),
    {
      fulfilledAt: opts.fulfilledAt
        ? Timestamp.fromDate(new Date(opts.fulfilledAt))
        : Timestamp.now(),
      fulfilledBy: window.AppState?.user?.email || 'unknown',
      deliveryMethod: opts.deliveryMethod || 'copy_email',
      identifiersScrubbed: opts.identifiersScrubbed === true,
      status: 'fulfilled',
      notes: opts.notes != null ? opts.notes : undefined
    }
  );
}

// ============================================
// MARK DENIED
// ============================================
export async function markDenied(tenantRaw, entryId, reason) {
  const tenant = resolveTenant(tenantRaw);
  await updateDoc(
    doc(db, 'tenants', tenant, 'recordsAccessLog', entryId),
    {
      status: 'denied',
      deliveryMethod: 'denied',
      fulfilledAt: Timestamp.now(),
      fulfilledBy: window.AppState?.user?.email || 'unknown',
      denialReason: reason || 'Not specified'
    }
  );
}

// ============================================
// VOID — soft-delete; preserves OSHA audit trail
// ============================================
export async function voidEntry(tenantRaw, entryId, reason) {
  const tenant = resolveTenant(tenantRaw);
  await updateDoc(
    doc(db, 'tenants', tenant, 'recordsAccessLog', entryId),
    {
      status: 'voided',
      voidedAt: Timestamp.now(),
      voidedBy: window.AppState?.user?.email || 'unknown',
      voidReason: reason || null
    }
  );
}

// ============================================
// ANNUAL RIGHT-OF-ACCESS NOTIFICATION (1910.1020(g)(1)(ii–iv))
// OSHA requires that each employee be informed — at initial assignment AND
// at least annually — of the existence/location/availability of their records,
// the records custodian's identity, and their right of access. Track this with
// a per-person `acknowledgedRecordsAccessNotificationAt` Firestore timestamp.
// ============================================

// Stamp the acknowledgment on a single person.
export async function stampRecordsAccessNotification(tenantRaw, personId, opts = {}) {
  const tenant = resolveTenant(tenantRaw);
  const ts = opts.notifiedAt
    ? Timestamp.fromDate(new Date(opts.notifiedAt))
    : Timestamp.now();
  await updateDoc(doc(db, 'tenants', tenant, 'people', personId), {
    acknowledgedRecordsAccessNotificationAt: ts,
    acknowledgedRecordsAccessNotificationBy: opts.notifiedBy || null,
    acknowledgedRecordsAccessNotificationNote: opts.note || null,
  });
}

// Returns { status, ageDays, dueDate } for one person.
// status: 'never' | 'due_soon' | 'overdue' | 'current'
// - never: no acknowledgment recorded
// - due_soon: 11-12 months since last (yellow)
// - overdue: >12 months since last (red)
// - current: <11 months since last (green)
export function getRecordsAccessNotificationStatus(person) {
  if (!person) return { status: 'never', ageDays: null, dueDate: null };
  const ts = person.acknowledgedRecordsAccessNotificationAt;
  if (!ts) return { status: 'never', ageDays: null, dueDate: null };
  const last = ts.toDate?.() || (typeof ts === 'string' ? new Date(ts) : (ts instanceof Date ? ts : null));
  if (!last || isNaN(last.getTime())) return { status: 'never', ageDays: null, dueDate: null };
  const now = new Date();
  const ageDays = Math.floor((now - last) / 86400000);
  const dueDate = new Date(last);
  dueDate.setFullYear(dueDate.getFullYear() + 1);
  // Use >= so day-365 is overdue (1910.1020(g)(1) says at least annually).
  if (ageDays >= 365) return { status: 'overdue', ageDays, dueDate };
  if (ageDays >= 335) return { status: 'due_soon', ageDays, dueDate }; // last ~30 days before due
  return { status: 'current', ageDays, dueDate };
}

// Walks the in-memory people list and returns those needing notification
// (never acknowledged OR overdue OR due soon). `peopleList` is the live
// people array — pass HRState.people via window.HRModule.getOshaPeopleForNotification()
// from outside the HR module; AppState.people is not populated in this codebase.
// Skips inactive/terminated people since the obligation attaches to active employment.
export function listPeopleNeedingNotification(peopleList) {
  if (!Array.isArray(peopleList)) return [];
  const out = [];
  for (const p of peopleList) {
    if (!p) continue;
    // Skip clearly-inactive people
    const status = String(p.employmentStatus || '').toLowerCase();
    const inactive = p.active === false || status === 'terminated' || status === 'inactive' || status === 'former';
    if (inactive) continue;
    const s = getRecordsAccessNotificationStatus(p);
    if (s.status === 'never' || s.status === 'overdue' || s.status === 'due_soon') {
      out.push({ person: p, ...s });
    }
  }
  // Sort: overdue first (oldest first), then never, then due_soon
  const rank = { overdue: 0, never: 1, due_soon: 2 };
  out.sort((a, b) => {
    const ra = rank[a.status] ?? 9;
    const rb = rank[b.status] ?? 9;
    if (ra !== rb) return ra - rb;
    return (b.ageDays || 0) - (a.ageDays || 0);
  });
  return out;
}

// ============================================
// SLA HELPER — returns { color, daysOpen, urgency }
// OSHA 1910.1020 requires fulfillment in 15 working days "where practicable"
// ============================================
export function getSlaStatus(entry) {
  if (!entry || !entry.requestedAt) return { color: '#94a3b8', daysOpen: 0, urgency: 'none' };
  if (entry.status !== 'pending') return { color: '#10b981', daysOpen: 0, urgency: 'closed' };
  const requestedAt = entry.requestedAt?.toDate?.() || new Date(entry.requestedAt);
  const now = new Date();
  // Approximate working days: total days * 5/7
  const totalDays = Math.floor((now - requestedAt) / 86400000);
  const workingDays = Math.floor(totalDays * 5 / 7);
  let color, urgency;
  if (workingDays < 10) { color = '#10b981'; urgency = 'on_track'; }
  else if (workingDays < 15) { color = '#f59e0b'; urgency = 'approaching'; }
  else { color = '#dc2626'; urgency = 'overdue'; }
  return { color, daysOpen: totalDays, workingDays, urgency };
}

// ============================================
// ANONYMIZED ANALYSIS LOG ENTRY (1910.1020(e)(2)(iii)(C))
// Writes the records-access-log entry that pairs with the
// `generateOshaAnonymizedAnalysis` callable. The callable itself is invoked
// from the modal in `app.js` (mirroring the cessation flow); this helper
// just opens the SLA loop in the log with `status:'pending'`. Admin marks
// fulfilled once they actually deliver the file to the designated rep —
// per COD Code Reviewer concern #2 (don't auto-mark fulfilled on export).
//
// Schema parity per concern #1: uses existing field names
// (`requesterName`, `requestSource`, `recordType`, `designatedRepName`).
// Pseudonymization is server-only per concern #3 — this helper never sees
// raw personIds; it just records that an anonymized export happened.
// ============================================
export async function logAnonymizedAnalysisRequest(tenantRaw, opts = {}) {
  const tenant = resolveTenant(tenantRaw);
  return await createAccessRequest(tenant, {
    requesterName: opts.requesterName || '',
    requestedForName: '(anonymized analysis — multi-person)',
    recordType: 'analysis',
    requestSource: 'designated_rep',
    designatedRepName: opts.requesterName || '',
    designatedRepAuthOnFile: opts.designatedRepAuthOnFile === true,
    notes: opts.notes
      ? `${opts.notes}\nAnonymized export · storagePath=${opts.storagePath || ''}`
      : `Anonymized export · storagePath=${opts.storagePath || ''}`,
  });
}

// Belt-and-suspenders: window-attached for cross-module access without import.
if (typeof window !== 'undefined') {
  window.OshaRecords = {
    createAccessRequest,
    listAccessLog,
    markFulfilled,
    markDenied,
    voidEntry,
    getSlaStatus,
    stampRecordsAccessNotification,
    getRecordsAccessNotificationStatus,
    listPeopleNeedingNotification,
    logAnonymizedAnalysisRequest,
  };
}
